import { useState } from "preact/hooks";
import type { Intent, TerminalState } from "../types";
import { fmtCanonicalPt, fmtDisplayName } from "../format";
import { StateBadge } from "./atoms/Badge";
import { buildEgoUrl, crmFinalLabel, getCrmFinalTarget } from "./CrmLinks";

interface TopbarProps {
  name?: string | null;
  email?: string | null;
  intent?: Intent | null;
  state?: TerminalState | null;
  entityId?: number | string | null;
  opportunityId?: number | string | null;
  opportunityUrl?: string | null;
  leadId?: number | string | null;
  leadUrl?: string | null;
  crmFinalKind?: string | null;
  shareUrl?: string | null;
  onCreateShareUrl?: (() => Promise<string | null>) | null;
  showBack?: boolean;
  showShare?: boolean;
}

/**
 * Botão "voltar":
 * - Se a página foi aberta a partir de same-origin (ex: clicou em "Abrir" no
 *   dashboard), faz history.back() — preserva scroll position da lista.
 * - Senão (referrer external, link copiado, WhatsApp deep-link) → /conversas.
 *
 * Quando virmos /leads ou outras entry pages, expande a lógica ou
 * parametriza via prop.
 */
const FALLBACK_URL = "/conversas";

function handleBack(e: MouseEvent) {
  e.preventDefault();
  try {
    const ref = document.referrer;
    if (ref) {
      const refUrl = new URL(ref);
      if (refUrl.origin === window.location.origin) {
        window.history.back();
        return;
      }
    }
  } catch {
    // ignora URL parse error
  }
  window.location.href = FALLBACK_URL;
}

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

export function Topbar(
  {
    name,
    email,
    intent,
    state,
    entityId,
    opportunityId,
    opportunityUrl,
    leadId,
    leadUrl,
    crmFinalKind,
    shareUrl,
    onCreateShareUrl,
    showBack = true,
    showShare = true,
  }: TopbarProps,
) {
  const [copied, setCopied] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const subtitle = [email, fmtCanonicalPt(intent)].filter(Boolean).join(" · ");
  const entityUrl = buildEgoUrl("person", entityId);
  const crmFinal = getCrmFinalTarget(
    crmFinalKind,
    opportunityId,
    leadId,
    opportunityUrl,
    leadUrl,
  );

  async function handleCopyLink() {
    if (copying) return;
    setCopying(true);
    setCopyError(false);
    try {
      const url = shareUrl || (onCreateShareUrl ? await onCreateShareUrl() : null);
      if (!url) return;
      await copyText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch (err) {
      console.error(err);
      setCopyError(true);
      window.setTimeout(() => setCopyError(false), 2000);
    } finally {
      setCopying(false);
    }
  }

  return (
    <header class="topbar">
      {showBack && (
        <a
          href={FALLBACK_URL}
          class="topbar-back"
          aria-label="Voltar"
          onClick={handleBack}
        >
          ←
        </a>
      )}
      <div class="topbar-title">
        <span class="topbar-name">{fmtDisplayName(name)}</span>
        {subtitle ? <span class="topbar-sub">{subtitle}</span> : null}
      </div>
      {(entityUrl || crmFinal.url) && (
        <div class="topbar-actions">
          {entityUrl && (
            <a class="topbar-action" href={entityUrl} target="_blank" rel="noopener">
              Entity eGO
            </a>
          )}
          {crmFinal.url && (
            <a class="topbar-action" href={crmFinal.url} target="_blank" rel="noopener">
              {crmFinalLabel(crmFinalKind, true)}
            </a>
          )}
        </div>
      )}
      {showShare && (shareUrl || onCreateShareUrl) && (
        <button
          class="topbar-copy"
          type="button"
          onClick={handleCopyLink}
          title="Copiar link da conversa"
          disabled={copying}
        >
          {copying ? "Gerando..." : copyError ? "Erro" : copied ? "Copiado" : "Copiar link"}
        </button>
      )}
      <StateBadge state={state} onDark />
    </header>
  );
}
